import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import BlockEditor, { BLOCK_TYPES } from "../../components/BlockEditor";
import { ArrowLeft, Save, Settings, Eye, UploadCloud, Loader2 } from "lucide-react";


export default function AdminBlogEditor() {
  const { postId } = useParams();
  const [post, setPost] = useState(null);
  const [savingMsg, setSavingMsg] = useState("");
  const [showMeta, setShowMeta] = useState(false);
  const [allCourses, setAllCourses] = useState([]);
  const [allPacks, setAllPacks] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadError, setUploadError] = useState("");

  const load = () => api.get(`/blog/admin/posts/${postId}`).then((r) => {
    const p = r.data || {};
    // Pré-remplit les champs SEO depuis le contenu déjà saisi pour éviter de
    // retaper. Titre SEO ← Titre ; Description SEO ← Résumé/extrait (ou sous-titre).
    // Reste modifiable si on veut une version SEO différente.
    if (!p.seo_title && p.title) p.seo_title = p.title;
    if (!p.seo_description) p.seo_description = p.excerpt || p.seo_subtitle || "";
    setPost(p);
  });
  useEffect(() => {
    load();
    api.get("/courses/admin/all").then((r) => setAllCourses(r.data)).catch(() => {});
    api.get("/packs/admin/all").then((r) => setAllPacks(r.data)).catch(() => {});
    api.get("/blog/admin/pillars").then((r) => setPillars(r.data || [])).catch(() => setPillars([]));
  }, [postId]);  // eslint-disable-line react-hooks/exhaustive-deps

  const updateField = (field, value) => setPost((p) => ({ ...p, [field]: value }));

  const seoUploadSlug = (post?.slug || post?.title || "")
    .toString()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/[\s_-]+/g, "-");

  const uploadCoverImage = async (file) => {
    if (!file) return;
    setUploadError("");

    if (!file.type?.startsWith("image/")) {
      setUploadError("Merci de sélectionner une image valide (JPG, PNG ou WebP).");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("context", "blog");
    formData.append("seo_slug", seoUploadSlug || "");
    formData.append("seo_title", post?.title || "");

    try {
      setUploadingCover(true);
      const { data } = await api.post("/files/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      updateField("cover_image", data.url || data.secure_url || "");
    } catch (err) {
      const message = err?.response?.data?.detail || "Impossible d’uploader l’image pour le moment.";
      setUploadError(message);
    } finally {
      setUploadingCover(false);
    }
  };


  const updateBlock = (idx, block) => {
    setPost((p) => {
      const blocks = [...(p.blocks || [])];
      blocks[idx] = block;
      return { ...p, blocks };
    });
  };
  const removeBlock = (idx) => setPost((p) => ({ ...p, blocks: (p.blocks || []).filter((_, i) => i !== idx) }));
  const moveBlock = (idx, dir) => setPost((p) => {
    const blocks = [...(p.blocks || [])];
    const j = idx + dir;
    if (j < 0 || j >= blocks.length) return p;
    [blocks[idx], blocks[j]] = [blocks[j], blocks[idx]];
    return { ...p, blocks };
  });
  const addBlock = (type) => {
    const block = { id: `blk_${Math.random().toString(36).slice(2, 12)}`, type, content: {} };
    setPost((p) => ({ ...p, blocks: [...(p.blocks || []), block] }));
  };

  const save = async () => {
    setSavingMsg("Sauvegarde...");
    const body = {
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      seo_subtitle: post.seo_subtitle || "",
      seo_pillar: post.seo_pillar || "",
      cover_image: post.cover_image,
      blocks: post.blocks,
      tags: post.tags,
      is_published: post.is_published,
      cta_course_id: post.cta_course_id || null,
      cta_pack_id: post.cta_pack_id || null,
      cta_text: post.cta_text || null,
      seo_title: post.seo_title || post.title || null,
      seo_description: post.seo_description || post.excerpt || post.seo_subtitle || null,
      display_order: Number(post.display_order ?? 100),
    };
    await api.patch(`/blog/posts/${postId}`, body);
    setSavingMsg("Enregistré ✓");
    setTimeout(() => setSavingMsg(""), 2000);
    load();
  };

  const togglePublish = async () => {
    await api.patch(`/blog/posts/${postId}`, { is_published: !post.is_published });
    load();
  };

  if (!post) return <div className="p-12">Chargement...</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 py-10" data-testid="admin-blog-editor-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <Link to="/admin/blog" className="text-sm text-gray-500 inline-flex items-center gap-1 hover:text-brand-green"><ArrowLeft className="w-4 h-4" /> Retour</Link>
        <div className="flex gap-2">
          {post.is_published && <Link to={`/blog/${post.slug}`} target="_blank" className="btn-ghost"><Eye className="w-4 h-4" /> Voir</Link>}
          <button onClick={() => setShowMeta(!showMeta)} className="btn-ghost" data-testid="blog-meta-toggle"><Settings className="w-4 h-4" /> Méta & SEO</button>
          <button onClick={togglePublish} className={post.is_published ? "btn-ghost" : "btn-orange"} data-testid="blog-publish-toggle">
            {post.is_published ? "Dépublier" : "Publier"}
          </button>
          <button onClick={save} className="btn-primary" data-testid="blog-save-btn"><Save className="w-4 h-4" /> Enregistrer</button>
        </div>
      </div>
      {savingMsg && <p className="text-sm text-emerald-600 mb-4">{savingMsg}</p>}

      <input
        className="input !text-3xl !font-heading !font-black w-full mb-3"
        value={post.title}
        onChange={(e) => updateField("title", e.target.value)}
        placeholder="Titre de l'article"
        data-testid="blog-title-edit"
      />
      <p className="text-xs text-gray-500 mb-3">URL : <code className="bg-emerald-50 px-2 py-0.5 rounded">/blog/{post.slug}</code></p>

      <input
        className="input mb-3"
        value={post.seo_subtitle || ""}
        onChange={(e) => updateField("seo_subtitle", e.target.value)}
        placeholder="Sous-titre SEO / angle problème"
        data-testid="blog-seo-subtitle-edit"
      />

      <textarea
        className="input mb-4"
        rows={2}
        value={post.excerpt || ""}
        onChange={(e) => updateField("excerpt", e.target.value)}
        placeholder="Résumé / extrait (description courte)"
        data-testid="blog-excerpt-edit"
      />

      <div className="card p-4 mb-6 space-y-3 border border-emerald-100 bg-white/80">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <label className="label">Image de couverture</label>
            <p className="text-xs text-gray-500">Upload Cloudinary ou URL externe. L’URL Cloudinary reste stable après redéploiement.</p>
          </div>
          <label className="btn-ghost cursor-pointer">
            {uploadingCover ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
            {uploadingCover ? "Upload..." : "Uploader une image"}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => uploadCoverImage(e.target.files?.[0])}
              disabled={uploadingCover}
              data-testid="blog-cover-upload-edit"
            />
          </label>
        </div>
        <input
          className="input"
          value={post.cover_image || ""}
          onChange={(e) => updateField("cover_image", e.target.value)}
          placeholder="URL de l'image de couverture"
          data-testid="blog-cover-edit"
        />
        {post.cover_image && (
          <div className="flex items-center gap-3 rounded-2xl border border-emerald-100 bg-emerald-50/60 p-3">
            <img src={post.cover_image} alt="Aperçu couverture" className="w-28 h-20 object-contain rounded-xl border border-white shadow-sm" />
            <p className="text-xs text-emerald-700 break-all">Image actuelle : {post.cover_image}</p>
          </div>
        )}
        {uploadError && <p className="text-sm text-red-600">{uploadError}</p>}
      </div>

      {showMeta && (
        <div className="card p-5 mb-6 space-y-3 border-l-4 border-brand-orange" data-testid="blog-meta-panel">
          <h2 className="font-heading text-lg font-bold">Métadonnées & SEO</h2>
          <div>
            <label className="label">Slug (URL)</label>
            <input className="input font-mono" value={post.slug} onChange={(e) => updateField("slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} data-testid="blog-slug-edit" />
          </div>
          <div>
            <label className="label">Pilier SEO</label>
            <select className="input" value={post.seo_pillar || ""} onChange={(e) => updateField("seo_pillar", e.target.value)} data-testid="blog-seo-pillar-edit">
              <option value="">— Aucun pilier —</option>
              {pillars.map((pillar) => <option key={pillar.pillar_id || pillar.name} value={pillar.name}>{pillar.name}</option>)}
            </select>
            <p className="text-xs text-gray-500 mt-1">Permet d’organiser le blog comme tes tableaux Pinterest et de renforcer tes clusters SEO. Les piliers se créent dans Admin &gt; Blog.</p>
          </div>
          <div>
            <label className="label">Tags (séparés par des virgules)</label>
            <input className="input" value={(post.tags || []).join(", ")} onChange={(e) => updateField("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))} data-testid="blog-tags-edit" />
          </div>
          <div>
            <label className="label">Ordre d'affichage</label>
            <input className="input" type="number" min="0" value={post.display_order ?? 100} onChange={(e) => updateField("display_order", e.target.value)} data-testid="blog-order-edit" />
            <p className="text-xs text-gray-500 mt-1">Plus le chiffre est petit, plus l’article apparaît haut sur la page blog. En cas d’égalité, la date de publication départage.</p>
          </div>
          <hr />
          <p className="font-semibold text-sm">SEO</p>
          <div>
            <label className="label">Titre SEO ({(post.seo_title || post.title || "").length}/60)</label>
            <input className="input" value={post.seo_title || ""} onChange={(e) => updateField("seo_title", e.target.value)} placeholder="Titre optimisé pour Google (60 chars max)" />
          </div>
          <div>
            <label className="label">Sous-titre SEO ({(post.seo_subtitle || "").length}/120)</label>
            <input className="input" value={post.seo_subtitle || ""} onChange={(e) => updateField("seo_subtitle", e.target.value)} placeholder="Angle clair orienté recherche Google" />
          </div>
          <div>
            <label className="label">Description SEO ({(post.seo_description || post.seo_subtitle || post.excerpt || "").length}/160)</label>
            <textarea className="input" rows={2} value={post.seo_description || ""} onChange={(e) => updateField("seo_description", e.target.value)} placeholder="Description meta (160 chars max)" />
          </div>
          <hr />
          <p className="font-semibold text-sm flex items-center gap-2">CTA contextuel <span className="text-xs text-gray-500">(formation ou pack à recommander en fin d'article)</span></p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="label">Formation associée</label>
              <select className="input" value={post.cta_course_id || ""} onChange={(e) => updateField("cta_course_id", e.target.value || null)} data-testid="blog-cta-course-select">
                <option value="">— Aucune —</option>
                {allCourses.map((c) => <option key={c.course_id} value={c.course_id}>{c.title}</option>)}
              </select>
            </div>
            <div>
              <label className="label">OU Pack associé</label>
              <select className="input" value={post.cta_pack_id || ""} onChange={(e) => updateField("cta_pack_id", e.target.value || null)} data-testid="blog-cta-pack-select">
                <option value="">— Aucun —</option>
                {allPacks.map((p) => <option key={p.pack_id} value={p.pack_id}>{p.title}</option>)}
              </select>
            </div>
          </div>
          <input className="input" value={post.cta_text || ""} onChange={(e) => updateField("cta_text", e.target.value)} placeholder="Texte du CTA (ex: 'Va plus loin avec notre programme')" />
        </div>
      )}

      <div className="space-y-3">
        {(post.blocks || []).map((b, i) => (
          <BlockEditor
            key={b.id}
            block={b}
            onChange={(blk) => updateBlock(i, blk)}
            onRemove={() => removeBlock(i)}
            onMove={(dir) => moveBlock(i, dir)}
            isFirst={i === 0}
            isLast={i === (post.blocks?.length || 0) - 1}
          />
        ))}
      </div>

      <div className="mt-6 card p-4">
        <p className="label mb-2">Ajouter un bloc</p>
        <div className="flex flex-wrap gap-2">
          {BLOCK_TYPES.map((b) => (
            <button key={b.type} onClick={() => addBlock(b.type)} className="btn-ghost !px-3 !py-1.5 text-sm" data-testid={`blog-add-block-${b.type}`}>+ {b.label}</button>
          ))}
        </div>
      </div>
    </div>
  );
}
